import React, { CSSProperties, ForwardedRef } from 'react';
import { StrategyCopyConstants } from 'crono-fe-common/constants/copyConstants/strategyCopyConstants';
import { PlaySmIcon, PlusSmIcon, StopSmIcon } from '@crono-one/design-system';
import { cn } from 'utils/style-utils';
import { OverlayTooltip, type OverlayProps } from 'components/Overlay';

export type SequenceButtonSize = 'xs' | 'small' | 'medium' | 'large';

const iconSizeClass: Record<SequenceButtonSize, string> = {
  xs: 'size-4',
  small: 'size-5',
  medium: 'size-6',
  large: 'size-9',
};

const paddingClass: Record<SequenceButtonSize, string> = {
  xs: 'p-1',
  small: 'p-1.25',
  medium: 'p-2',
  large: 'p-3',
};

type Action = 'add' | 'start' | 'stop';

/** Background and icon colours, then their hover, per action. */
const actionClass = (
  action: Action,
  whiteHover: boolean,
  hover: boolean,
): string => {
  switch (action) {
    case 'add':
      return cn(
        'bg-primary-100 text-primary-700',
        hover && 'hover:bg-primary-200 hover:text-primary-800',
      );
    case 'start':
      return cn(
        'bg-primary-100 text-green-600',
        hover &&
          (whiteHover
            ? 'hover:bg-green-600 hover:text-white'
            : 'hover:bg-primary-200'),
      );
    case 'stop':
      return cn(
        whiteHover ? 'bg-white' : 'bg-orange-200',
        'text-orange-700',
        hover &&
          (whiteHover
            ? 'hover:bg-orange-700 hover:text-white'
            : 'hover:bg-orange-300'),
      );
  }
};

const tooltipText = (action: Action) =>
  action === 'add'
    ? StrategyCopyConstants.ADD_TO_STRATEGY_TEXT
    : action === 'start'
      ? StrategyCopyConstants.RESUME_INSTANCE_TEXT
      : null;

interface SequenceInstanceButtonProps {
  onClick?: () => void;
  action: Action;
  iconButtonStyle?: CSSProperties;
  size?: SequenceButtonSize;
  iconFill?: string;
  disableHover?: boolean;
  disableTooltip?: boolean;
  disabled?: boolean;
  disabledTooltipText?: string;
  whiteHover?: boolean;
  /** Pass it when the button sits in a table cell. */
  overlayProps?: OverlayProps;
}

const SequenceInstanceButton = React.forwardRef(
  (props: SequenceInstanceButtonProps, ref: ForwardedRef<HTMLDivElement>) => {
    const {
      onClick,
      action,
      iconButtonStyle,
      size = 'medium',
      iconFill,
      disableHover = false,
      disableTooltip,
      disabled = false,
      disabledTooltipText,
      whiteHover = false,
      overlayProps,
      ...rest
    } = props;

    const Icon =
      action === 'add'
        ? PlusSmIcon
        : action === 'start'
          ? PlaySmIcon
          : StopSmIcon;
    // The resume button keeps a tight padding at every size.
    const padding =
      action === 'start' && size !== 'xs' ? 'p-1.25' : paddingClass[size];

    return (
      <div ref={ref} {...rest} className="flex justify-center">
        <OverlayTooltip
          placement="top"
          delayDuration={200}
          overlayProps={overlayProps}
          content={
            disabled && disabledTooltipText
              ? disabledTooltipText
              : disableTooltip
                ? null
                : tooltipText(action)
          }
        >
          <div className="flex size-fit items-center justify-center">
            <button
              type="button"
              aria-label={tooltipText(action) ?? 'Stop sequence'}
              className={cn(
                'flex cursor-pointer items-center justify-center rounded-full border-0 transition-colors',
                padding,
                disabled
                  ? 'cursor-default bg-neutral-100 text-neutral-400'
                  : actionClass(action, whiteHover, !disableHover),
              )}
              style={iconButtonStyle}
              disabled={disabled}
              onClick={() => onClick?.()}
            >
              <Icon
                className={iconSizeClass[size]}
                style={!disabled && iconFill ? { color: iconFill } : undefined}
              />
            </button>
          </div>
        </OverlayTooltip>
      </div>
    );
  },
);

export default SequenceInstanceButton;
